import { useQuery } from '@tanstack/react-query'
import type { OrgLocationRowView } from '@today/ui-domain'
import { type OrgLocationDTO, organizationService } from '@/services/organization.service'
import {
	buildLocationCreateBody,
	buildLocationPatch,
	EMPTY_LOCATION_FORM,
	type LocationFormValues,
	locationFormErrors,
	locationToForm,
} from './location-form'
import {
	listState,
	type OrgT,
	useConfirmState,
	useDialogMutations,
	useEditorState,
	viewConfirm,
	viewEditor,
} from './org-editor'

const toRow = (l: OrgLocationDTO): OrgLocationRowView => ({
	id: l.id,
	locationType: l.locationType,
	addressLine1: l.addressLine1,
	postalCode: l.postalCode,
	city: l.city,
	countryCode: l.countryCode,
})

/** A unit's Locations panel (REQ-007): list, add/edit dialog and guarded delete. */
export function useOrgLocations({ unitId, t }: { unitId: string; t: OrgT }) {
	const queryKey = ['org-locations', unitId] as const
	const list = useQuery({ queryKey, queryFn: () => organizationService.locations(unitId) })
	const form = useEditorState<LocationFormValues, { original: OrgLocationDTO | null }>(t)
	const deleting = useConfirmState(t)
	const m = useDialogMutations({
		queryKey,
		form,
		deleting,
		save: ({ values, extra: { original } }) =>
			original
				? organizationService.updateLocation(unitId, original.id, buildLocationPatch(original, values))
				: organizationService.createLocation(unitId, buildLocationCreateBody(values)),
		remove: (id) => organizationService.deleteLocation(unitId, id),
	})
	const byId = (id: string) => list.data?.find((l) => l.id === id)
	return {
		state: listState(list),
		rows: (list.data ?? []).map(toRow),
		retry: () => void list.refetch(),
		editor: viewEditor(form.editor),
		deleting: viewConfirm(deleting.pending),
		isBusy: m.isBusy,
		openAdd: () => form.open('create', EMPTY_LOCATION_FORM, { original: null }),
		openEdit: (id: string) => {
			const original = byId(id)
			if (original) form.open('edit', locationToForm(original), { original })
		},
		change: (field: keyof LocationFormValues, value: string) =>
			form.change(field, value as LocationFormValues[typeof field]),
		close: form.close,
		submit: () => m.submit(locationFormErrors),
		askDelete: (id: string) => deleting.ask(id, byId(id)?.addressLine1 ?? ''),
		cancelDelete: deleting.cancel,
		confirmDelete: m.confirmDelete,
	}
}
